import React, { FC } from 'react'
import { useTranslation } from 'react-i18next'
import {
  TextInput,
  Button,
  Heading,
  Label,
  Paragraph,
  PhoneInput,
} from '@pandiafi/design-system'
import { Formik, Field, Form, FieldProps, FormikProps } from 'formik'

import {
  User,
  UserMetadata,
  UserUpdateRequest,
  userUpdateSchema,
} from '../../../common/validations'
import { useModuleConfig } from '../../hooks/useModuleConfig/useModuleConfig'
import { UserInfoToggleField } from './UserInfoToggleField'

import FormFieldWrapper from 'components/FormField'
import { FormFieldInfoTooltip } from 'components/FormFieldInfoTooltip/FormFieldInfoTooltip'
import { validateFormValues } from 'utils'

interface UserInfoFormProps {
  user: User
  onSave: (userUpdateRequest: UserUpdateRequest) => void
  onCancel: () => void
  isPending: boolean
  userMetadata: UserMetadata
}

type FormValues = {
  email: string
  phoneNumber: string
  communicationByEmail: boolean
  communicationBySms: boolean
  marketingAllowed: boolean
  eServicesConsent: boolean
}

export const UserInfoForm: FC<UserInfoFormProps> = ({
  user,
  onSave,
  onCancel,
  isPending,
  userMetadata: { isImpersonator },
}) => {
  const { t } = useTranslation(['common'])
  const {
    userInfo: { showCommunicationPreferences, showElectronicServicesConsent },
  } = useModuleConfig()

  const handleValidate = (formValues: FormValues) => {
    const allValidationErrors = validateFormValues(formValues, userUpdateSchema)

    return allValidationErrors
  }

  return (
    <Formik
      initialValues={{
        email: user.email ?? '',
        phoneNumber: user.phoneNumber ?? '',
        communicationByEmail: user.communicationByEmail ?? true,
        communicationBySms: user.communicationBySms ?? true,
        marketingAllowed: user.marketingAllowed ?? false,
        eServicesConsent: user.eServicesConsent,
      }}
      onSubmit={({ eServicesConsent, ...values }) =>
        onSave({
          ...values,
          // Only a changed consent is sent, so saving a form opened before
          // a change made elsewhere does not undo that change
          ...(eServicesConsent !== user.eServicesConsent
            ? { eServicesConsent }
            : {}),
        })
      }
      validate={handleValidate}
    >
      {(form: FormikProps<FormValues>) => (
        <Form>
          <div className="flex flex-col space-y-2 text-sm mb-4">
            <Label className="font-medium">{t('residentId')}</Label>
            <Paragraph className="[word-break:break-word] text-sm">
              {user.cognitoId ?? ''}
            </Paragraph>
          </div>
          <Field name="email">
            {({ field, meta }: FieldProps<string>) => (
              <FormFieldWrapper
                name="email"
                label={t('emailForContact')}
                labelNormal
                meta={meta}
                required
              >
                <TextInput id="email" type="email" {...field} width={320} />
              </FormFieldWrapper>
            )}
          </Field>
          <Field name="phoneNumber">
            {({ field, meta }: FieldProps<string>) => (
              <FormFieldWrapper
                name="phoneNumber"
                label={t('phone')}
                labelNormal
                meta={meta}
                required
              >
                <PhoneInput
                  id="phoneNumber"
                  value={field.value || ''}
                  onChange={(value) => {
                    form.setFieldTouched('phoneNumber', true)
                    form.setFieldValue('phoneNumber', value.replace(/\s/g, ''))
                    setTimeout(() => form.validateField('phoneNumber'), 100)
                  }}
                  className="inline-block"
                />
              </FormFieldWrapper>
            )}
          </Field>
          <Paragraph className="my-4">
            {t('common:userInfo.description1')}
          </Paragraph>
          <Paragraph>{t('common:userInfo.description2')}</Paragraph>

          {showCommunicationPreferences && (
            <div className="mt-8">
              <Heading
                element="h2"
                size="l"
                className="font-medium mb-4 flex items-center"
              >
                {t('common:waysToCommunicate')}
                <FormFieldInfoTooltip
                  text={t('common:userInfo.communicationPreferencesInfo')}
                />
              </Heading>
              <div className="flex flex-col gap-4 items-end">
                <UserInfoToggleField
                  name="communicationByEmail"
                  label={t('common:communicationByEmail')}
                />
                <UserInfoToggleField
                  name="communicationBySms"
                  label={t('common:communicationBySms')}
                />
              </div>
            </div>
          )}

          <div className="mt-8 mb-14">
            <Heading element="h2" size="l" className="font-medium mb-4">
              {t('common:userInfo.permissionsHeader')}
            </Heading>
            <div className="flex flex-col gap-4 items-end">
              <UserInfoToggleField
                name="marketingAllowed"
                label={t('common:marketingAllowed')}
                description={t('common:userInfo.marketingAllowedDescription')}
              />
              {showElectronicServicesConsent && (
                <UserInfoToggleField
                  name="eServicesConsent"
                  label={t('common:userInfo.electronicServicesConsent')}
                  description={t(
                    'common:userInfo.electronicServicesConsentDescription'
                  )}
                />
              )}
            </div>
          </div>

          <div className="flex justify-end mt-4">
            <Button type="button" variant="cancel" onClick={onCancel}>
              {t('cancel')}
            </Button>
            <Button
              type="submit"
              className="mr-2"
              loading={isPending}
              disabled={isImpersonator || isPending || !form.isValid}
            >
              {t('save')}
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  )
}
